import React from 'react';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { WordPressEvent } from '../../services/wordpress';
import EventDetails from './EventDetails';
import { formatEventDate } from '../../utils/dateUtils';
import { getEventImageUrl, getEventPath, getEventTitle } from '../../utils/eventUtils';
import { useLocalization } from '../../localization';

interface EventRowProps {
  event: WordPressEvent;
  isExpanded: boolean;
  toggleExpand: (eventId: number, eventName: string) => void;
  currentMonth: string;
  primColorHue: number;
  showTicketLink?: boolean;
}

const EventRow: React.FC<EventRowProps> = ({
  event,
  isExpanded,
  toggleExpand,
  currentMonth,
  primColorHue,
  showTicketLink = false,
}) => {
  const { copy, locale } = useLocalization();
  const navigate = useNavigate();
  const formattedTitle = getEventTitle(event);
  const eventPath = getEventPath(event);
  const hasMeta = Boolean(event.acf?.price_description || event.acf?.genre);
  const mobileMeta = [
    event.acf?.price_description,
    event.acf?.genre,
  ]
    .filter(Boolean)
    .join(' · ');

  const handleRowClick = () => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      navigate(eventPath);
    }
  };

  return (
    <div
      id={`event-${event.id}`}
      className={classNames('event-row', { expanded: isExpanded })}
      data-month={currentMonth}
      onClick={handleRowClick}
    >
      <div className="event-image">
        {isExpanded ? (
          <Link to={eventPath} onClick={(eventClick) => eventClick.stopPropagation()}>
            <img
              src={getEventImageUrl(event)}
              alt={formattedTitle}
            />
          </Link>
        ) : (
          <img
            src={getEventImageUrl(event)}
            alt={formattedTitle}
          />
        )}
      </div>
      <div className="event-details">
        <Link
          to={eventPath}
          onClick={(eventClick) => eventClick.stopPropagation()}
        >
          <h5 className="event-title desktop">
            {formatEventDate(
              event.acf?.event_date,
              event.acf?.event_time,
              event.acf?.closing_time,
              'default',
              locale
            )}
            {` - `}
            {formattedTitle}
          </h5>
        </Link>
        <Link to={eventPath} onClick={(eventClick) => eventClick.stopPropagation()}>
          <h5 className="event-title mobile">
            {formatEventDate(
              event.acf?.event_date,
              event.acf?.event_time,
              event.acf?.closing_time,
              'mobile-list',
              locale
            )}
            {<br />}
            {formattedTitle}
          </h5>
        </Link>
        <p className="event-description">
          {new DOMParser()
            .parseFromString(event.content.rendered, 'text/html')
            .body.textContent?.replace(/\n+/g, '\n\n')
            .split(/(https?:\/\/[^\s]+)/g) // Split text into parts by URLs
            .map((part, index) =>
              part.match(/https?:\/\/[^\s]+/) ? (
                <a
                  key={index}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {part}
                </a>
              ) : (
                <span key={index}>{part}</span>
              )
            )}
        </p>
        {isExpanded && (
          <div id={`event-details-${event.id}`}>
            <EventDetails
              event={event}
              isExpanded={isExpanded}
              primColorHue={primColorHue}
            />
          </div>
        )}
        <div className="event-footer">
          {hasMeta ? (
            <div className="event-meta">
              <p className="text-muted desktop">
                {event.acf?.price_description ? `${event.acf.price_description} · ` : ''}
                {event.acf?.genre || ''}
              </p>
              <p className="text-muted mobile">
                {mobileMeta}
              </p>
            </div>
          ) : (
            <div className="event-meta event-meta-empty" aria-hidden="true" />
          )}
          {showTicketLink && event.acf?.link_tickets ? (
            <a
              href={event.acf.link_tickets}
              target="_blank"
              rel="noopener noreferrer"
              className="event-ticket-link-mobile"
              onClick={(eventClick) => eventClick.stopPropagation()}
            >
              {copy.events.tickets}
            </a>
          ) : null}
          <div className="event-actions">
            {showTicketLink && event.acf?.link_tickets ? (
              <a
                href={event.acf.link_tickets}
                target="_blank"
                rel="noopener noreferrer"
                className="event-ticket-link"
                onClick={(eventClick) => eventClick.stopPropagation()}
              >
                {copy.events.tickets}
              </a>
            ) : null}
            <button
              type="button"
              className="event-expand-button"
              onClick={() => toggleExpand(event.id, event.title.rendered)}
              aria-expanded={isExpanded}
              aria-controls={`event-details-${event.id}`}
            >
              {isExpanded ? copy.events.closeSummary : copy.events.moreInfo}
            </button>
            <Link to={eventPath} className="event-page-link">
              {copy.events.viewEvent}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventRow;
